/**
 * Pure helpers for decoding the auth JWT supplied by the host or standalone login.
 * No signature verification — the gateway enforces that; these helpers only pull UI hints.
 */

export interface JwtClaims {
  UserId?: string;
  userId?: string;
  OrganizationID?: string;
  organizationId?: string;
  exp?: number;
  [key: string]: unknown;
}

/** Base64url → JSON. Returns null when the token is malformed or not present. */
export function decodeJwt(token: string | null | undefined): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const json = typeof atob === 'function' ? atob(padded) : '';
    if (!json) return null;
    const decoded = decodeURIComponent(
      Array.prototype.map.call(json, (c: string) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`).join('')
    );
    return JSON.parse(decoded) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * Returns the numeric / string user id encoded in the token (claim `UserId` or `userId`),
 * or null when the token is absent/invalid.
 */
export function getUserIdFromToken(token: string | null | undefined): string | null {
  const claims = decodeJwt(token);
  if (!claims) return null;
  const id = claims.UserId ?? claims.userId;
  if (typeof id === 'string' && id.length > 0) return id;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return null;
}
